'use client';

import React from 'react';
import Link from 'next/link';

export default function ReportPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">

        {/* URGENT HEADER */}
        <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6 mb-8">
          <h1 className="text-3xl font-bold text-red-900 mb-4">
            🚨 Report Suspected Trafficking or Exploitation
          </h1>
          <p className="text-red-800 text-lg mb-4">
            If you suspect someone is being trafficked, exploited, or forced to create content,
            report it immediately. We take all reports seriously and will investigate within 24 hours.
          </p>
          <p className="text-red-900 font-bold">
            This is a ZERO TOLERANCE platform. Anyone found to be trafficking, coercing, or
            exploiting performers will be immediately banned and reported to law enforcement.
          </p>
        </div>

        {/* URGENT INDICATORS */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            🚩 Signs Someone May Be Trafficked
          </h2>
          <p className="text-gray-700 mb-4">
            Look for these warning signs:
          </p>
          <ul className="space-y-2 text-gray-700 ml-6 list-disc">
            <li>Someone else controls their account, documents, or earnings</li>
            <li>They appear to be reading from a script or coached</li>
            <li>They seem fearful, anxious, or under duress</li>
            <li>They can't freely leave or stop creating content</li>
            <li>Their identification or travel documents are held by someone else</li>
            <li>Someone else is always present during filming</li>
            <li>They mention threats, violence, or debt bondage</li>
            <li>Multiple performers share the same location, IP address, or manager</li>
            <li>They appear to be underage (under 18 years old)</li>
          </ul>
        </div>

        {/* HOW TO REPORT */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            📞 How to Report
          </h2>

          <div className="space-y-6">
            {/* Report to JuicyStarz */}
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Report to JuicyStarz
              </h3>
              <p className="text-gray-700 mb-3">
                Email us immediately with any evidence:
              </p>
              <div className="bg-gray-50 p-4 rounded border">
                <p className="font-mono text-sm">
                  <strong>Email:</strong> <a href="mailto:report@juicystarz.com" className="text-blue-600 hover:text-blue-800">report@juicystarz.com</a>
                </p>
                <p className="font-mono text-sm mt-2">
                  <strong>Subject:</strong> URGENT TRAFFICKING REPORT
                </p>
              </div>
              <p className="text-gray-600 mt-3 text-sm">
                Include: Username, profile URL, description of concerns, screenshots/videos if possible.
                We will investigate within 24 hours and suspend accounts if warranted.
              </p>
            </div>

            {/* Report to National Hotline */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                National Human Trafficking Hotline
              </h3>
              <p className="text-gray-700 mb-3">
                Call or text 24/7 for confidential help:
              </p>
              <div className="bg-blue-50 p-4 rounded border border-blue-300">
                <p className="text-2xl font-bold text-blue-900">
                  1-888-373-7888
                </p>
                <p className="text-blue-800 mt-2">
                  Text: 233733 (BEFREE)
                </p>
              </div>
              <p className="text-gray-600 mt-3 text-sm">
                The National Human Trafficking Hotline is available 24/7 in multiple languages.
              </p>
            </div>

            {/* Report to NCMEC */}
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                National Center for Missing & Exploited Children (NCMEC)
              </h3>
              <p className="text-gray-700 mb-3">
                Report child sexual exploitation material:
              </p>
              <div className="bg-purple-50 p-4 rounded border border-purple-300">
                <p className="font-mono">
                  <a
                    href="https://report.cybertip.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-700 hover:text-purple-900 underline font-semibold"
                  >
                    CyberTipline: report.cybertip.org
                  </a>
                </p>
                <p className="text-purple-800 mt-2">
                  Phone: 1-800-843-5678
                </p>
              </div>
              <p className="text-gray-600 mt-3 text-sm">
                NCMEC works with law enforcement to investigate child exploitation.
              </p>
            </div>

            {/* Report to Law Enforcement */}
            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Local Law Enforcement
              </h3>
              <p className="text-gray-700 mb-3">
                For immediate danger or emergencies:
              </p>
              <div className="bg-yellow-50 p-4 rounded border border-yellow-300">
                <p className="text-2xl font-bold text-yellow-900">
                  Call 911
                </p>
              </div>
              <p className="text-gray-600 mt-3 text-sm">
                If someone is in immediate danger, call 911 or your local emergency number.
              </p>
            </div>
          </div>
        </div>

        {/* WHAT HAPPENS NEXT */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            ⚖️ What Happens After You Report
          </h2>
          <div className="space-y-4 text-gray-700">
            <div className="flex gap-3">
              <span className="text-2xl">1️⃣</span>
              <div>
                <h3 className="font-bold">Immediate Review</h3>
                <p>Our team reviews all reports within 24 hours. Critical reports are escalated immediately.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-2xl">2️⃣</span>
              <div>
                <h3 className="font-bold">Account Suspension</h3>
                <p>If we find evidence of trafficking or exploitation, the account is immediately suspended.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-2xl">3️⃣</span>
              <div>
                <h3 className="font-bold">Law Enforcement Notification</h3>
                <p>We report suspected trafficking to the National Human Trafficking Hotline, FBI, and local law enforcement.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-2xl">4️⃣</span>
              <div>
                <h3 className="font-bold">Ongoing Investigation</h3>
                <p>We preserve all evidence and cooperate fully with law enforcement investigations.</p>
              </div>
            </div>
          </div>
        </div>

        {/* CONFIDENTIALITY */}
        <div className="bg-blue-50 border border-blue-300 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-blue-900 mb-3">
            🔒 Your Report is Confidential
          </h2>
          <p className="text-blue-800">
            We do not share reporter identities. You can report anonymously.
            We only share evidence with law enforcement when investigating trafficking cases.
          </p>
        </div>

        {/* OUR COMMITMENT */}
        <div className="bg-gray-100 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            ✊ Our Commitment
          </h2>
          <p className="text-gray-700 mb-4">
            JuicyStarz has a zero-tolerance policy for trafficking, exploitation, and coercion.
            We are committed to:
          </p>
          <ul className="space-y-2 text-gray-700 ml-6 list-disc">
            <li><strong>Proactive Monitoring:</strong> We use automated and manual systems to detect trafficking indicators</li>
            <li><strong>Rapid Response:</strong> All reports are reviewed within 24 hours</li>
            <li><strong>Law Enforcement Cooperation:</strong> We work closely with FBI, ICE, NCMEC, and local authorities</li>
            <li><strong>Victim Support:</strong> We connect victims with resources and assistance</li>
            <li><strong>Account Termination:</strong> Traffickers are permanently banned from our platform</li>
          </ul>
        </div>

        {/* RESOURCES */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            📚 Additional Resources
          </h2>
          <div className="space-y-3 text-gray-700">
            <div>
              <h3 className="font-bold">Polaris Project</h3>
              <p className="text-sm">Comprehensive anti-trafficking resources and support</p>
              <a
                href="https://polarisproject.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                polarisproject.org
              </a>
            </div>
            <div>
              <h3 className="font-bold">FBI Human Trafficking</h3>
              <p className="text-sm">Report trafficking to the FBI online</p>
              <a
                href="https://www.fbi.gov/investigate/violent-crime/human-trafficking"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                fbi.gov/investigate/violent-crime/human-trafficking
              </a>
            </div>
            <div>
              <h3 className="font-bold">Department of Homeland Security Blue Campaign</h3>
              <p className="text-sm">Federal resources for identifying and reporting trafficking</p>
              <a
                href="https://www.dhs.gov/blue-campaign"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                dhs.gov/blue-campaign
              </a>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="text-center text-gray-600 text-sm">
          <p className="mb-2">
            JuicyStarz is committed to combating human trafficking and exploitation.
          </p>
          <p>
            <Link href="/" className="text-blue-600 hover:text-blue-800 underline">
              Return to Home
            </Link>
            {' · '}
            <Link href="/terms" className="text-blue-600 hover:text-blue-800 underline">
              Terms of Service
            </Link>
            {' · '}
            <Link href="/2257" className="text-blue-600 hover:text-blue-800 underline">
              18 USC § 2257 Compliance
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
}
